/**
 * Facet hover-prefetch + pre-parse (PLP / search).
 *
 * When the pointer dwells on a desktop filter option, we do the two expensive
 * steps of a filter change up front, off the click:
 *   1. fetch the filtered section HTML (same URL facets.js would request), and
 *   2. parse it into a Document.
 * Both are stashed by URL. The real click then only has to clone the parsed tree
 * and inject it — the network + parse are already done.
 *
 * Integration with Dawn's facets.js (no edits to it):
 *   - We prime FacetFiltersForm.filterData with the raw html, so its own
 *     renderSectionFromCache path fires (renders from cache, no network).
 *   - We wrap that cache path so facets.js's three internal DOMParser calls
 *     (grid, count, filters) return clones of our pre-parsed Document instead of
 *     re-parsing the string. Clones because facets.js relocates nodes out of the
 *     parsed tree, so each consumer needs its own fresh copy.
 *
 * Classic script (not a module) so it shares the global scope with facets.js and
 * can read/patch the `FacetFiltersForm` class directly. Load AFTER facets.js.
 *
 * Hover-only (pointer: fine) — touch devices never dwell, so this is a no-op there.
 */
(() => {
  if (window.__facetPrefetchWired) return; // guard against double-wiring on hot reload
  window.__facetPrefetchWired = true;

  const DWELL_MS = 500;
  const SORT_FILTER_FORM_IDS = ['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'];
  const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
  const NativeDomParser = window.DOMParser; // captured so our transient swap never self-parses

  const getFacetFiltersForm = () => (typeof FacetFiltersForm !== 'undefined' ? FacetFiltersForm : window.FacetFiltersForm);

  // url -> pre-parsed Document, produced during hover so the click only clones + injects.
  const parsedDocumentsByUrl = new Map();
  let cacheRenderPatched = false;

  let dwellTimer = null;
  let dwellLabel = null;

  /**
   * The exact `searchParams` string facets.js's onSubmitHandler would build if
   * this checkbox were toggled — same forms, same order. We flip .checked to read
   * FormData (identical to createSearchParams), then flip it back. Setting
   * .checked programmatically dispatches no input/change events, so nothing else
   * reacts and the UI never shows the transient state.
   */
  function toggledSearchParams(checkbox) {
    checkbox.checked = !checkbox.checked;
    try {
      const formParams = [];
      document.querySelectorAll('facet-filters-form form').forEach((form) => {
        if (!SORT_FILTER_FORM_IDS.includes(form.id)) return;
        formParams.push(new URLSearchParams(new FormData(form)).toString());
      });
      return formParams.join('&');
    } finally {
      checkbox.checked = !checkbox.checked;
    }
  }

  /**
   * Run facets.js's render while its internal `new DOMParser().parseFromString()`
   * calls return clones of a pre-parsed Document. Scoped to one synchronous render
   * (facets.js parses + relocates nodes synchronously), then restored.
   */
  function renderFromParsedDocument(parsedDocument, render) {
    if (!parsedDocument) return render();

    const StubbedDomParser = function () {};
    StubbedDomParser.prototype.parseFromString = () => parsedDocument.cloneNode(true);
    window.DOMParser = StubbedDomParser;
    try {
      render();
    } finally {
      window.DOMParser = NativeDomParser;
    }
  }

  /** Wrap facets.js's cache-render path once so hovered facets skip re-parsing on click. */
  function ensureCacheRenderPatched() {
    if (cacheRenderPatched) return;
    const facetFiltersForm = getFacetFiltersForm();
    if (!facetFiltersForm || typeof facetFiltersForm.renderSectionFromCache !== 'function') return;

    const originalRenderSectionFromCache = facetFiltersForm.renderSectionFromCache.bind(facetFiltersForm);
    facetFiltersForm.renderSectionFromCache = function (matchesCachedUrl, event, updateEvent) {
      const cacheEntry = facetFiltersForm.filterData.find(matchesCachedUrl);
      const parsedDocument = cacheEntry ? parsedDocumentsByUrl.get(cacheEntry.url) : null;
      renderFromParsedDocument(parsedDocument, () => originalRenderSectionFromCache(matchesCachedUrl, event, updateEvent));
    };
    cacheRenderPatched = true;
  }

  /** Cache a fetched result under `url`: the string (for facets.js) and the parsed Document. */
  function cacheFetchedResult(url, html) {
    const facetFiltersForm = getFacetFiltersForm();
    if (!facetFiltersForm) return;

    facetFiltersForm.filterData = facetFiltersForm.filterData || [];
    if (!facetFiltersForm.filterData.some((cacheEntry) => cacheEntry.url === url)) {
      facetFiltersForm.filterData = [...facetFiltersForm.filterData, { html, url }];
    }
    if (!parsedDocumentsByUrl.has(url)) {
      parsedDocumentsByUrl.set(url, new NativeDomParser().parseFromString(html, 'text/html'));
    }
  }

  function prefetch(checkbox) {
    const facetFiltersForm = getFacetFiltersForm();
    const productGrid = document.getElementById('product-grid');
    if (!facetFiltersForm || !productGrid) return;
    ensureCacheRenderPatched();

    const searchParams = toggledSearchParams(checkbox);
    const url = `${window.location.pathname}?section_id=${productGrid.dataset.id}&${searchParams}`;
    if (parsedDocumentsByUrl.has(url)) return; // already fetched + parsed

    fetch(url)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((html) => cacheFetchedResult(url, html))
      .catch(() => {
        /* prefetch is best-effort; a miss just means the click fetches normally */
      });
  }

  document.addEventListener('pointerover', (event) => {
    if (!hoverCapable.matches) return;
    const label = event.target.closest('#FacetFiltersForm .facets__label');
    if (!label || label === dwellLabel) return;

    const checkbox = label.querySelector('input[type="checkbox"]:not([disabled])');
    if (!checkbox) return;

    dwellLabel = label;
    clearTimeout(dwellTimer);
    dwellTimer = setTimeout(() => prefetch(checkbox), DWELL_MS);
  });

  document.addEventListener('pointerout', (event) => {
    const label = event.target.closest('#FacetFiltersForm .facets__label');
    // Only cancel when the pointer truly leaves the label (not moving between its children).
    if (label && label === dwellLabel && !label.contains(event.relatedTarget)) {
      clearTimeout(dwellTimer);
      dwellLabel = null;
    }
  });

  // Press-to-prefetch: pointerdown fires before the click/change, so this kicks off
  // the fetch immediately for clicks that never waited out the hover dwell. The
  // checkbox hasn't toggled yet at pointerdown, so the toggle simulation still
  // matches. The has()-guard in prefetch() dedupes against an earlier hover fetch.
  document.addEventListener('pointerdown', (event) => {
    const label = event.target.closest('#FacetFiltersForm .facets__label');
    if (!label) return;
    const checkbox = label.querySelector('input[type="checkbox"]:not([disabled])');
    if (checkbox) prefetch(checkbox);
  });

  ensureCacheRenderPatched();
})();
